import { useCallback } from 'react';
import { formatValue, formatValueArrays, NOT_AVAILABLE } from './helpers';
import { useTracking } from './TrackingContext';
import { publish } from './pubsub';
import type { EventData } from 'src/components/containers/PremiumExperienceGrid/eventsData';

interface State {
  budget?: number | undefined;
  date?: undefined | string[];
  ticketOffers?: undefined | React.Key[];
  sessions?: undefined | React.Key[];
  experience?: undefined | React.Key[];
  arenas?: undefined | React.Key[];
  sort?: undefined | string;
}

const filterMapper = (payload: Partial<State>) => {
  const mapper = {
    date: "eventDateFilter",
    sessions: "eventSessionFilter",
    ticketOffers: "eventExperienceCategoryFilter",
    experience: "eventExperienceFilter",
    arenas: "eventLocationFilter",
  }

  let keyArr = '';
  Object.entries(mapper).forEach(([key, value]) => {
    if (key === payload) {
      keyArr = value;
    }
  });

  const arr = [
    {
      id: "filterName",
      value: `select-${payload}`
    },
    {
      id: keyArr,
      value: NOT_AVAILABLE
    },
  ];
  const result = arr.reduce((obj, cur) => ({ ...obj, [cur.id]: cur.value }), {})
  return result
};

export function useTrackingChange() {
  const container = useTracking();
  const { eventType = 'premium-experience-grid' } = container;

  const track = useCallback(
    (state: State, action: $TSFixMe) => {
      const baseline: Record<string, unknown> = {
        currencyCode: container.currencyCode,
        promoCode: formatValue(container.promoCode),
        siteEdition: container.siteEdition,
        eventDateFilter: formatValueArrays(state.date),
        eventSessionFilter: formatValueArrays(state.sessions),
        eventExperienceCategoryFilter: formatValueArrays(state.ticketOffers),
        eventExperienceFilter: formatValueArrays(state.experience),
        eventLocationFilter: formatValueArrays(state.arenas)
      };

      // Tag name is optional
      if (container.tagName) {
        baseline.tagName = container.tagName;
      }

      const send = (name: string, changes: Record<string, unknown>) => {
        publish(`em.${eventType}.${name}`, {
          ...baseline,
          ...changes
        });
      };

      const payload: Partial<State> = { ...action.payload };
      switch (action.type) {
        case 'RESET_FILTER': {
          send('reset-filter', filterMapper(action.payload));
          break;
        }
        case 'SEARCH': {
          const payload = action.payload as EventData;
          send('search-initiation', {
            eventName: payload.title,
            eventLocation: payload.locationName,
            eventSession: payload.sessionType,
            eventExperienceCategory: payload.offerLabel,
            eventExperience: payload.title,
            label: payload.ctaLabel,
            url: payload.ctaUrl,
            eventDate: payload.isoDate,
            totalPrice: payload.price,
            totalPriceUSD: payload.price
          });
          break;
        }
        case 'FILTER_CHANGE': {
          if (payload.budget) {
            send('change-budget', { budgetMaximumAmount: payload.budget });
          }
          if (payload.date) {
            send('select-date', { eventDateFilter: formatValueArrays(payload.date) });
          }
          if (payload.ticketOffers) {
            send('select-experience', { eventExperienceCategoryFilter: formatValueArrays(payload.ticketOffers) });
          }
          if (payload.arenas) {
            send('select-location', { eventLocationFilter: formatValueArrays(payload.arenas) });
          }
          if (payload.sort) {
            send('sort', { sortBy: payload.sort });
          }
          if (payload.sessions) {
            send('select-session', { eventSessionFilter: formatValueArrays(payload.sessions) });
          }
          if (payload.experience) {
            send('select-experience-name', { eventExperienceFilter: formatValueArrays(payload.experience) });
          }
          break;
        }
        default: {
          return undefined;
        }
      }
    },
    [container, eventType]
  );

  return track;
}
